import React, { useEffect, useState } from 'react';
import { tenantAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Loader2, FileText, Pencil, Download, Eye, Inbox } from 'lucide-react';
import { toast } from 'sonner';

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function TenantDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingNote, setViewingNote] = useState(null);
  const [loadingNote, setLoadingNote] = useState(false);

  useEffect(() => {
    loadDocs();
  }, []);

  const loadDocs = async () => {
    setLoading(true);
    try {
      const res = await tenantAPI.listMyDocuments();
      setDocs(res.data || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewNote = async (doc) => {
    setLoadingNote(true);
    try {
      const res = await tenantAPI.getMyDocument(doc.doc_id);
      setViewingNote(res.data);
    } catch (error) {
      toast.error('Failed to load note');
    } finally {
      setLoadingNote(false);
    }
  };

  const handleDownload = async (doc) => {
    if (doc.type === 'file' && doc.s3_key) {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/s3/${doc.s3_key}`;
      window.open(url, '_blank');
      return;
    }
    if (doc.type === 'note') {
      try {
        const res = await tenantAPI.getMyDocument(doc.doc_id);
        const blob = new Blob([res.data.content || ''], { type: 'text/plain;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${(doc.name || 'note').replace(/[^\w. -]/g, '_')}.txt`;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch {
        toast.error('Failed to download note');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#C85A42]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenant-documents-page">
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold flex items-center gap-2">
          <FileText className="w-7 h-7 text-[#C85A42]" />
          My Documents
        </h1>
        <p className="text-muted-foreground mt-1">
          Files and notes your landlord has shared with you. Read-only.
        </p>
      </div>

      {docs.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Inbox className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg">No documents yet</h3>
            <p className="text-sm text-muted-foreground mt-1">
              When your landlord shares contracts, receipts, or notes with you, they'll appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {docs.map((doc) => (
            <Card key={doc.doc_id} className="hover:shadow-md transition-shadow" data-testid={`tenant-document-${doc.doc_id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${doc.type === 'note' ? 'bg-violet-100 dark:bg-violet-900/40' : 'bg-blue-100 dark:bg-blue-900/40'}`}>
                    {doc.type === 'note' ? (
                      <Pencil className="w-5 h-5 text-violet-600" />
                    ) : (
                      <FileText className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-base truncate" title={doc.name}>{doc.name}</CardTitle>
                    <CardDescription className="text-xs mt-1">
                      {doc.type === 'note' ? 'Note' : (doc.mime_type || 'File')}
                      {doc.size_bytes != null && ` · ${formatBytes(doc.size_bytes)}`}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-xs text-muted-foreground mb-3">
                  Shared {formatDate(doc.created_at)}
                </p>
                <div className="flex gap-2">
                  {doc.type === 'note' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleViewNote(doc)}
                      disabled={loadingNote}
                      data-testid={`view-document-${doc.doc_id}`}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1.5" /> View
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => handleDownload(doc)}
                    data-testid={`download-document-${doc.doc_id}`}
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Note viewer dialog */}
      <Dialog open={!!viewingNote} onOpenChange={() => setViewingNote(null)}>
        <DialogContent className="max-w-3xl" data-testid="view-note-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              {viewingNote?.name}
            </DialogTitle>
            <DialogDescription>
              Shared {viewingNote?.created_at ? formatDate(viewingNote.created_at) : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-white dark:bg-slate-900 border rounded-lg p-6 max-h-[60vh] overflow-y-auto">
            <pre className="whitespace-pre-wrap font-serif text-base leading-relaxed">
              {viewingNote?.content || ''}
            </pre>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleDownload(viewingNote)}>
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
            <Button onClick={() => setViewingNote(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
